import React, { useMemo } from "react";
import styled from "@emotion/styled";
import { css, Global, keyframes } from "@emotion/react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { useLocation, useSearchParams } from "react-router-dom";
import qs from "qs";
import oneSolomonLogo from "../../assets/onesolomon-logo-black.png";
import { parseBoolean } from "../../utils/parseBoolean";

export const oneSolomonColors = {
  yellow: "#FFDD41",
  oldWhite: "#FFF8D9",
  ochre: "#3C2415",
  ochreSoft: "#5C3A24",
  ochreLine: "rgba(60, 36, 21, 0.15)",
  ochreLineStrong: "rgba(60, 36, 21, 0.35)",
  infoBlue: "#DCEEF1",
} as const;

export const ONE_SOLOMONS_COMPANY_UUID =
  "1c943adb-fb2e-40b6-8926-77ab54dc125a";

export function normalizeCompanyUuid(value: unknown): string | undefined {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (Array.isArray(value) && typeof value[0] === "string" && value[0].trim()) {
    return value[0].trim();
  }
  return undefined;
}

export function isOneSolomonsCompanyUuid(
  companyUuid?: string | null
): boolean {
  return (companyUuid?.trim() ?? "") === ONE_SOLOMONS_COMPANY_UUID;
}

export function resolveIsCompanyOneSolomons(
  companyUuid?: string | null,
  ...flagSources: Array<string | boolean | null | undefined>
): boolean {
  if (
    companyUuid !== undefined &&
    companyUuid !== null &&
    String(companyUuid).trim() !== ""
  ) {
    return isOneSolomonsCompanyUuid(companyUuid);
  }
  for (const value of flagSources) {
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return parseBoolean(value);
    }
  }
  return false;
}

export function isCompanyOneSolomonsFlag(
  companyUuid?: string | null,
  ...flagSources: Array<string | boolean | null | undefined>
): boolean {
  return resolveIsCompanyOneSolomons(companyUuid, ...flagSources);
}

const ONE_SOLOMON_SESSION_KEY = "isCompanyOneSolomons";
const COMPANY_UUID_SESSION_KEY = "company_uuid";

/** Persists One Solomon mode across in-app navigations that drop query params. */
export function readOneSolomonModeFromSession(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.sessionStorage.getItem(ONE_SOLOMON_SESSION_KEY) === "true";
  } catch {
    return false;
  }
}

export function writeOneSolomonModeToSession(value: boolean): void {
  if (typeof window === "undefined") return;
  try {
    if (value) {
      window.sessionStorage.setItem(ONE_SOLOMON_SESSION_KEY, "true");
    } else {
      window.sessionStorage.removeItem(ONE_SOLOMON_SESSION_KEY);
    }
  } catch {
    // sessionStorage may be unavailable (private mode, quota); fall back silently.
  }
}

/** Persists the company_uuid so white-labeling survives URL params being dropped. */
export function readCompanyUuidFromSession(): string | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const value = window.sessionStorage.getItem(COMPANY_UUID_SESSION_KEY);
    return value && value.trim() ? value.trim() : undefined;
  } catch {
    return undefined;
  }
}

export function writeCompanyUuidToSession(value?: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (typeof value === "string" && value.trim()) {
      window.sessionStorage.setItem(COMPANY_UUID_SESSION_KEY, value.trim());
    } else {
      window.sessionStorage.removeItem(COMPANY_UUID_SESSION_KEY);
    }
  } catch {
    // sessionStorage may be unavailable (private mode, quota); fall back silently.
  }
}

export function useIsCompanyOneSolomonsFromSearch(): boolean {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  return useMemo(() => {
    const query = qs.parse(searchParams.toString());
    const initialValues = query?.initialValues as
      | {
          company_uuid?: string;
          isCompanyOneSolomons?: boolean | string;
        }
      | undefined;

    // A fresh order payload is authoritative. If it does not carry a
    // company_uuid, do not inherit a previous One Solomon session.
    if (initialValues) {
      return resolveIsCompanyOneSolomons(
        normalizeCompanyUuid(initialValues.company_uuid)
      );
    }

    const companyUuidTop = normalizeCompanyUuid(query?.company_uuid);
    const fromSearch = resolveIsCompanyOneSolomons(
      companyUuidTop ?? readCompanyUuidFromSession(),
      query?.isCompanyOneSolomons as string | boolean | undefined
    );
    // Layout sits above the Order route, so internal navigations like
    // navigate("/payment") drop the query string and the search-only check
    // returns false. Fall back to the sessionStorage flag that Order writes
    // once it has resolved the company.
    return fromSearch || readOneSolomonModeFromSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.pathname]);
}

/** Paints html/body/#root with the One Solomon page background. */
export const OneSolomonPageBackground = () => (
  <Global
    styles={css`
      html,
      body,
      #root {
        background-color: ${oneSolomonColors.oldWhite} !important;
        min-height: 100%;
      }
    `}
  />
);

export const OneSolomonFonts = () => (
  <Helmet>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link
      rel="preconnect"
      href="https://fonts.gstatic.com"
      crossOrigin="anonymous"
    />
    <link
      href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700;800;900&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;1,9..144,300;1,9..144,400&display=swap"
      rel="stylesheet"
    />
  </Helmet>
);

export const OneSolomonShell = styled.div`
  color: ${oneSolomonColors.ochre};
  font-family: "Archivo", sans-serif;
  font-size: 16px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  background: ${oneSolomonColors.oldWhite};
  min-height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const OneSolomonCard = styled.div<{ $maxWidthPx?: number }>`
  max-width: ${({ $maxWidthPx }) => ($maxWidthPx != null ? $maxWidthPx : 540)}px;
  width: 100%;
  margin: 0 auto;
  padding: 56px 56px 48px;

  @media (max-width: 640px) {
    padding: 36px 24px 32px;
  }
`;

export const OneSolomonBrand = styled.div`
  text-align: center;
  margin-bottom: 40px;

  img {
    max-width: 220px;
    width: 100%;
    height: auto;
  }
`;

export function OneSolomonLogo() {
  return (
    <OneSolomonBrand>
      <img src={oneSolomonLogo} alt="ONE Solomon" />
    </OneSolomonBrand>
  );
}

export const OneSolomonHeading = styled.h1`
  text-align: center;
  font-family: "Archivo", sans-serif;
  font-weight: 900;
  font-size: 36px;
  letter-spacing: -0.025em;
  line-height: 1.05;
  color: ${oneSolomonColors.ochre};
  margin: 0 0 14px;

  @media (max-width: 640px) {
    font-size: 28px;
  }
`;

export const OneSolomonSubheading = styled.p`
  text-align: center;
  font-family: "Fraunces", serif;
  font-style: italic;
  font-weight: 400;
  font-size: 18px;
  color: ${oneSolomonColors.ochre};
  margin: 0 0 36px;
  line-height: 1.4;

  @media (max-width: 640px) {
    font-size: 16px;
    margin-bottom: 28px;
  }
`;

export const OneSolomonForm = styled.form`
  display: flex;
  flex-direction: column;
`;

export const OneSolomonFieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;

  @media (max-width: 640px) {
    gap: 12px;
  }
`;

export const OneSolomonField = styled.div<{ inRow?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: ${({ inRow }) => (inRow ? 0 : "14px")};
`;

export const OneSolomonFieldLabel = styled.label`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: ${oneSolomonColors.ochreSoft};
`;

const fieldInputStyles = css`
  background: #fff;
  border: 1.5px solid ${oneSolomonColors.ochreLineStrong};
  border-radius: 8px;
  padding: 14px 16px;
  font-family: "Archivo", sans-serif;
  font-size: 16px;
  color: ${oneSolomonColors.ochre};
  outline: none;
  width: 100%;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: ${oneSolomonColors.ochreSoft};
    opacity: 0.55;
  }

  &:focus {
    border-color: ${oneSolomonColors.ochre};
    box-shadow: 0 0 0 3px rgba(255, 221, 65, 0.4);
  }

  &:disabled,
  &[readonly] {
    background: rgba(60, 36, 21, 0.04);
    border-style: dashed;
    border-color: ${oneSolomonColors.ochreLineStrong};
    color: ${oneSolomonColors.ochreSoft};
    font-variant-numeric: tabular-nums;
    cursor: default;
    box-shadow: none;
  }

  @media (max-width: 640px) {
    font-size: 15px;
    padding: 13px 14px;
  }
`;

export const OneSolomonInput = styled.input<{
  hasError?: boolean;
  borderless?: boolean;
}>`
  ${fieldInputStyles}
  ${({ hasError }) =>
    hasError &&
    css`
      border-color: #b42318;
    `}
  ${({ borderless }) =>
    borderless &&
    css`
      border: none;
      padding: 0;
      background: transparent;
      box-shadow: none !important;

      &:focus {
        box-shadow: none !important;
        border-color: transparent;
      }
    `}
`;

export const OneSolomonFieldError = styled.span`
  font-size: 12px;
  color: #b42318;
`;

export const OneSolomonAgreement = styled.p`
  font-family: "Fraunces", serif;
  font-style: italic;
  font-weight: 400;
  font-size: 15px;
  line-height: 1.5;
  color: ${oneSolomonColors.ochre};
  margin: 22px 0;
  padding-left: 16px;
  border-left: 2px solid ${oneSolomonColors.yellow};

  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

export const OneSolomonDisclaimer = styled.div`
  background: ${oneSolomonColors.infoBlue};
  border-radius: 10px;
  padding: 14px 16px 14px 38px;
  font-size: 13px;
  line-height: 1.5;
  margin: 16px 0 28px;
  position: relative;
  color: ${oneSolomonColors.ochre};

  &::before {
    content: "i";
    position: absolute;
    left: 14px;
    top: 14px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${oneSolomonColors.ochre};
    color: ${oneSolomonColors.infoBlue};
    font-size: 11px;
    font-weight: 700;
    font-family: "Fraunces", serif;
    font-style: italic;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 640px) {
    font-size: 12px;
  }
`;

export const OneSolomonCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

export const OneSolomonCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: ${oneSolomonColors.ochre};
  cursor: pointer;
  user-select: none;
`;

export const OneSolomonCheckboxInput = styled.input`
  appearance: none;
  -webkit-appearance: none;
  width: 22px;
  height: 22px;
  border: 1.5px solid ${oneSolomonColors.ochre};
  border-radius: 5px;
  background: transparent;
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: background 0.15s ease;
  margin: 0;

  &:checked {
    background: ${oneSolomonColors.yellow};
  }

  &:checked::after {
    content: "";
    position: absolute;
    left: 6px;
    top: 2px;
    width: 6px;
    height: 12px;
    border: solid ${oneSolomonColors.ochre};
    border-width: 0 2.5px 2.5px 0;
    transform: rotate(45deg);
  }

  &:focus-visible {
    outline: 3px solid rgba(255, 221, 65, 0.5);
    outline-offset: 2px;
  }
`;

export const OneSolomonConfirmButton = styled.button`
  width: 100%;
  background: ${oneSolomonColors.ochre};
  color: ${oneSolomonColors.yellow};
  border: 0;
  padding: 22px 28px;
  border-radius: 999px;
  font-family: "Archivo", sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: background 0.15s ease, transform 0.05s ease;

  &:hover:not(:disabled) {
    background: #2a1810;
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 640px) {
    padding: 18px 22px;
    font-size: 15px;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const OneSolomonSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid ${oneSolomonColors.ochreLine};
  border-top-color: ${oneSolomonColors.ochre};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  margin: 48px auto;
`;

export const OneSolomonPaymentHeading = styled(OneSolomonHeading)`
  margin-bottom: 36px;

  @media (max-width: 640px) {
    margin-bottom: 28px;
  }
`;

export const OneSolomonHostedField = styled.div`
  background: #fff;
  border: 1.5px solid ${oneSolomonColors.ochreLineStrong};
  border-radius: 8px;
  padding: 14px 16px;
  min-height: 52px;
  display: flex;
  align-items: center;
  gap: 10px;

  input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-family: "Archivo", sans-serif;
    font-size: 16px;
    color: ${oneSolomonColors.ochre};
    padding: 0;

    &::placeholder {
      color: ${oneSolomonColors.ochreSoft};
      opacity: 0.55;
    }

    &:focus {
      outline: none;
      box-shadow: none;
    }
  }

  @media (max-width: 640px) {
    font-size: 15px;
    padding: 13px 14px;
  }
`;

export const OneSolomonSawfishRoot = styled.div`
  width: 100%;

  input,
  select,
  textarea,
  button {
    font-family: "Archivo", sans-serif;
  }
`;

export const OneSolomonRecaptchaNote = styled.p`
  margin-top: 24px;
  text-align: center;
  font-size: 11px;
  color: ${oneSolomonColors.ochreSoft};
  opacity: 0.7;
  line-height: 1.5;

  a {
    color: ${oneSolomonColors.ochre};
    text-decoration: underline;
  }
`;

export const OneSolomonAlert = styled.div<{
  variant: "error" | "success" | "warning" | "info";
}>`
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.5;
  ${({ variant }) => {
    switch (variant) {
      case "error":
        return css`
          background: #fdecea;
          color: #611a15;
        `;
      case "warning":
        return css`
          background: #fff4e5;
          color: #663c00;
        `;
      case "info":
        return css`
          background: ${oneSolomonColors.infoBlue};
          color: ${oneSolomonColors.ochre};
        `;
      default:
        return css`
          background: #e8f5e9;
          color: #1b5e20;
        `;
    }
  }}
`;

type OneSolomonPaymentPageProps = {
  title: string;
  children: React.ReactNode;
};

export function OneSolomonPaymentPage({
  title,
  children,
}: OneSolomonPaymentPageProps) {
  return (
    <OneSolomonShell>
      <OneSolomonFonts />
      <OneSolomonCard>
        <OneSolomonLogo />
        <OneSolomonPaymentHeading>{title}</OneSolomonPaymentHeading>
        {children}
      </OneSolomonCard>
    </OneSolomonShell>
  );
}

export function OneSolomonContentPage({
  children,
  cardMaxWidthPx,
}: {
  children: React.ReactNode;
  /** When set, overrides the default OneSolomonCard max-width (540px). */
  cardMaxWidthPx?: number;
}) {
  return (
    <OneSolomonShell>
      <OneSolomonFonts />
      <OneSolomonCard $maxWidthPx={cardMaxWidthPx}>
        <OneSolomonLogo />
        {children}
      </OneSolomonCard>
    </OneSolomonShell>
  );
}

export function ConfirmOrderArrowIcon() {
  return (
    <svg
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3 11L11 3M11 3H5M11 3v6"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function formatAudAmount(amount: number): string {
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
  }).format(amount);
}
